import {
  ChangeEvent,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import cx from 'classnames';
import { omit } from 'ramda';
import OutsideClickHandler from 'react-outside-click-handler';
//@ts-ignore
import Select, { components } from 'react-select';

import { FormFieldWrapper } from 'components/shared/BaseFormComponents';
import Icon from 'components/shared/Icon';
import ArrowUp from 'components/shared/Icon/icons/ArrowUp';
import Close from 'components/shared/Icon/icons/Close';
import Plus from 'components/shared/Icon/icons/Plus';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import { useEscAction } from 'hooks/useEscAction';

import s from './../../../../../shared/BaseFormComponents/Select/index.module.css';
import styles from './index.module.css';
import { getSelectCustomStyles } from './styles';
import { IRoomInfo, IRoomsSelectProps } from './types';

export const RoomsSelect: FC<IRoomsSelectProps> = ({
  options,
  onOptionsChange,
  onChange,
  placeholder,
  helpText,
  errorMessage,
  defaultOption,
  optionActionIcon,
  onOptionActionClick,
  onOptionCreation,
}) => {
  const [roomInfo, setRoomInfo] = useState<IRoomInfo>({});
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');

  const ref = useRef(null);

  useEscAction(() => handleCloseMenu());

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const roomCountHandler = (roomCount: IRoomInfo) => setRoomInfo((prev) => ({ ...prev, ...roomCount }));

  const handleOptionCreation = useCallback(() => {
    const id = inputValue + options.length;
    const createdOption = { value: id, label: inputValue, isCustom: true };

    onOptionCreation(createdOption);
    //@ts-ignore
    onChange([...defaultOption, createdOption]);
    setInputValue('');
  }, [inputValue]);

  //@ts-ignore
  const handleSelectChange = (option, { action, removedValue, option: clickedOption }) => {
    switch (action) {
      case 'select-option':
        onChange(option);
        !clickedOption?.count && roomCountHandler({ [clickedOption.value]: 0 });
        break;
      case 'deselect-option':
        setRoomInfo((prev) => omit([clickedOption.value], prev));
        onChange(option);
        break;
      case 'remove-value':
        setRoomInfo((prev) => omit([removedValue.value], prev));
        onChange(option);
        break;
      case 'clear':
        handleInputValue('');
        onChange([]);
        setRoomInfo({});
        handleCloseMenu();
        break;
      default:
        return;
    }
  };

  const handleInputValue = (value: string) => {
    setIsMenuOpen(true);
    setInputValue(value);
  };

  const handleInputBlur = () => {
    //@ts-ignore
    ref.current?.blur();
  };

  const OptionMemo = useCallback(
    //@ts-ignore
    (props) => (
      <Option
        onActionClick={onOptionActionClick}
        actionIcon={optionActionIcon}
        onRoomInfoInput={roomCountHandler}
        triggerBlur={handleInputBlur}
        {...props}
      />
    ),
    [onOptionActionClick],
  );

  const MenuMemo = useCallback(
    //@ts-ignore
    (props) => <Menu showCreateOptionButton onOptionCreation={handleOptionCreation} {...props} />,
    [handleOptionCreation],
  );

  useEffect(() => {
    const setOptionCount = (option: any) => ({ ...option, count: roomInfo[option.value] ?? option?.count });
    //@ts-ignore
    onOptionsChange((prev) => prev.map(setOptionCount));
    //@ts-ignore
    onChange(defaultOption?.map(setOptionCount));
  }, [roomInfo]);

  return (
    <FormFieldWrapper errorMessage={errorMessage} helpText={helpText}>
      <OutsideClickHandler
        onOutsideClick={() => {
          handleInputValue('');
          handleCloseMenu();
        }}
        useCapture={false}
      >
        <Select
          ref={ref}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Enter' && inputValue) {
              event.preventDefault();
              handleOptionCreation();
            }
          }}
          value={defaultOption}
          placeholder={placeholder}
          components={{
            Menu: MenuMemo,
            Input,
            NoOptionsMessage,
            Option: onOptionActionClick ? OptionMemo : components.Option,
            // FIXME: add type after react-select library updating to version 5
            DropdownIndicator: (props: any) => <DropdownIndicator {...props} isRotated={isMenuOpen} />,
            ClearIndicator,
            MultiValueRemove,
            MultiValueLabel,
          }}
          menuIsOpen={isMenuOpen}
          inputValue={inputValue}
          onChange={handleSelectChange}
          onInputChange={(value: string, { action }: { action: string }) => {
            if (action === 'input-change') {
              handleInputValue(value);
            }
          }}
          onFocus={() => setIsMenuOpen(true)}
          options={options}
          isMulti
          isClearable
          hideSelectedOptions={false}
          backspaceRemovesValue={false}
          styles={getSelectCustomStyles(errorMessage)}
        />
      </OutsideClickHandler>
    </FormFieldWrapper>
  );
};

//@ts-ignore
const MultiValueLabel = ({ data, ...props }) => {
  const { label, count } = data;
  const countPart = count && count > 1 ? ` x ${count}` : '';

  return <components.MultiValueLabel {...props}>{label + countPart}</components.MultiValueLabel>;
};

//@ts-ignore
const MultiValueRemove = (props) => (
  <components.MultiValueRemove {...props}>
    <Icon component={Close} width="1.4rem" height="1.4rem" className={styles.removeIcon} />
  </components.MultiValueRemove>
);

//@ts-ignore
const Option = ({ children, data, onRoomInfoInput, onActionClick, triggerBlur, actionIcon, ...props }) => {
  const [value, setValue] = useState(data?.count || '');

  const roomsCountHandler = (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

  const inputRef = useRef<HTMLInputElement>(null);

  const inputClickHandler: MouseEventHandler<HTMLInputElement> = (e) => {
    e.stopPropagation();
    inputRef.current?.focus();
  };

  const enterHandler: KeyboardEventHandler<HTMLInputElement> = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      //@ts-ignore
      e.target.blur();
    }
  };

  return (
    <components.Option {...props} data={data}>
      <div className={styles.option}>
        <input type="checkbox" checked={props.isSelected} onChange={() => {}} />
        {children}
      </div>
      <div className={styles.optionActions}>
        <div className={styles.roomsCount}>
          <input
            ref={inputRef}
            onKeyDown={enterHandler}
            onClick={inputClickHandler}
            type="text"
            placeholder="# of rooms"
            value={value}
            onChange={roomsCountHandler}
            onBlur={(e) => onRoomInfoInput({ [data.value]: Number(e.target.value) })}
            disabled={!props.isSelected}
          />
        </div>
        {onActionClick && (
          <Icon
            component={actionIcon}
            disabled={data.isCustom}
            width="1.8rem"
            height="1.8rem"
            onClick={(event) => {
              event.stopPropagation();
              triggerBlur();
              onActionClick(data.value);
            }}
          />
        )}
      </div>
    </components.Option>
  );
};

//@ts-ignore
const Menu = ({ children, anchorRef, onOptionCreation, selectProps, ...props }) => {
  const { inputValue } = selectProps;

  return (
    <components.Menu innerRef={anchorRef} {...props}>
      {children}
      <div
        className={s.createButton}
        onClick={() => {
          if (inputValue) {
            onOptionCreation();
          }
        }}
      >
        <Icon component={Plus} fill="#212121" />
        <span className={s.createButtonText}>Create</span>
        {inputValue && <span className={s.createButtonValueText}>{` "${inputValue}"`}</span>}
      </div>
      {/* @ts-ignore */}
      <MenuActionsHelper isNew />
    </components.Menu>
  );
};

//@ts-ignore
const DropdownIndicator = ({ isRotated, ...props }) => (
  <components.DropdownIndicator {...props}>
    <Icon
      component={ArrowUp}
      className={cx(s.selectIcon, { [s['selectIcon-noRotated']]: isRotated })}
      data-testid="roomSelectDropdownIndicator"
    />
  </components.DropdownIndicator>
);

//@ts-ignore
const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <Icon component={Close} />
  </components.ClearIndicator>
);

//@ts-ignore
const NoOptionsMessage = (props) => (
  <components.NoOptionsMessage {...props}>
    <div className={s.noOptionsMessage}>No options found</div>
  </components.NoOptionsMessage>
);

//@ts-ignore
const Input = (props) => <components.Input {...props} isHidden={false} data-testid="roomSelectInput" />;
